import React, { useEffect, useState } from 'react';

import ProcurementExpensesDetailedRow from './ProcurementExpensesDetailedRow';

import { formatFigure, formatNegativeMoney } from 'Helpers/formatService';

export default function ProcurementExpensesDetailedTable(props) {
    const [originalTotal, setOriginalTotal] = useState(0);

    useEffect(() => {
        if (props.uncommittedExpenditures) {
            setOriginalTotal(sumRenderedRowsTotal(props.uncommittedExpenditures));
        }
    }, [props.uncommittedExpenditures]);

    // Sum detailed_amount.original_total for every row we render (matches Budgeted Total column).
    // Ensures custom pack items are included in the footer total.
    const sumRenderedRowsTotal = (expenditures) => {
        if (!expenditures || expenditures.length === 0) return 0;
        return expenditures.reduce((acc, item) => {
            const hasDetailedAmount = !!item.detailed_amount;
            const hasChildren = item.uncommitted_expenditures?.length > 0;
            // Recurse when there are children (same as render: we don't render a row for parents)
            if (hasChildren) {
                return acc + sumRenderedRowsTotal(item.uncommitted_expenditures);
            }
            if (hasDetailedAmount && item.detailed_amount?.type !== 'project') {
                return acc + (Number(item.detailed_amount?.original_total) || 0);
            }
            return acc;
        }, 0);
    };

    const quantitiesNames = Array.from({ length: props.maxQuantities }, (_, i) => $t(`Quantity ${i + 1}`));

    const formattedTotalVariance = () => {
        return formatFigure(originalTotal < props.total ? -Math.abs(props.variance) : Math.abs(props.variance), formatNegativeMoney);
    };

    const renderUncommittedExpenditures = (expenditures, parentIndex = '') => {
        return expenditures
            .map((expenditure, index) => {
                const key = `${parentIndex}-${index}`;
                const hasDetailedAmount = !!expenditure.detailed_amount;
                const hasChildren = expenditure.uncommitted_expenditures?.length > 0;

                // If no detailed amount but has children, process them recursively
                if (hasChildren) {
                    return renderUncommittedExpenditures(expenditure.uncommitted_expenditures, key);
                } else if (hasDetailedAmount) {
                    // If it has a detailed amount, render the row
                    return (
                        <ProcurementExpensesDetailedRow
                            key={key}
                            uncommittedExpenditure={expenditure}
                            index={key}
                            maxQuantities={props.maxQuantities}
                            varianceExists={props.varianceExists}
                        />
                    );
                }

                return null;
            })
            .flat(); // Flatten in case of nested arrays
    };

    return (
        <>
            <div>
                <div className="table__card tw-mt-4">
                    <div>
                        <h5 className="table__header">Confirmed Commitment</h5>
                    </div>
                    <div className="inbud-table_wrapper tw-overflow-auto tw-mt-6">
                        <table className="inbud-table" style={{ minWidth: 'max-content' }}>
                            <thead>
                                <tr>
                                    <th className="tw-w-56">{$t('Department / Cost Centre')}</th>
                                    <th className="tw-w-56">{$t('Budget Line')}</th>
                                    <th className="tw-w-36">{$t('Budgeted Price')}</th>
                                    {quantitiesNames.map((name, index) => {
                                        return (
                                            <th className="tw-w-36" key={`original-${index}`}>
                                                {$t('Budgeted') + ' ' + name}
                                            </th>
                                        );
                                    })}
                                    <th className="tw-w-36">{$t('Budgeted Total')}</th>
                                    {props.varianceExists && (
                                        <>
                                            <th className="tw-w-36">{$t('Revised Price')}</th>
                                            {quantitiesNames.map((name, index) => {
                                                return (
                                                    <th className="tw-w-36" key={`revised-${index}`}>
                                                        {$t('Revised') + ' ' + name}
                                                    </th>
                                                );
                                            })}
                                            <th className="tw-w-36">{$t('Revised Total')}</th>
                                            <th className="tw-w-36">{$t('Variance')}</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>{renderUncommittedExpenditures(props.uncommittedExpenditures)}</tbody>
                            <tfoot className="tw-bg-blue">
                                <tr>
                                    <td>{$t('Total')}</td>
                                    <td colSpan={props.maxQuantities + 2}></td>
                                    <td className="inbud-numeric-cell">{formatFigure(originalTotal)}</td>
                                    {props.varianceExists && (
                                        <>
                                            <td colSpan={props.maxQuantities + 1}></td>
                                            <td className="inbud-numeric-cell">{formatFigure(props.total)}</td>
                                            <td className="inbud-numeric-cell">{formattedTotalVariance()}</td>
                                        </>
                                    )}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
}
